'use client';

import { useState, useCallback, useEffect } from 'react';
import type { AsyncResult, AppError } from '@shared/utils';
import { tryCatchAsync, err, ok } from '@shared/utils';
import { Logger } from '@shared/logger';

/**
 * @group Hooks
 * @summary
 * <blockquote>Hook for managing camera MediaStream lifecycle</blockquote>
 * 
 * @example
 * ```tsx
 * const { stream, isActive, error, startCamera, stopCamera } = useWebcam();
 * 
 * const handleStart = async () => {
 *   const result = await startCamera();
 *   result.match({
 *     ok: () => console.log('Camera started'),
 *     err: (e) => console.error(e.errorMessage),
 *   });
 * };
 * ```
 */
const CAMERA_WIDTH_IDEAL = 1280;
const CAMERA_WIDTH_MIN = 640;
const CAMERA_HEIGHT_IDEAL = 720;
const CAMERA_HEIGHT_MIN = 480;
const CAMERA_FPS_IDEAL = 15;
const CAMERA_FPS_MAX = 30;

interface UseWebcamReturn {
  stream: MediaStream | null;
  isActive: boolean;
  error: AppError | null;
  startCamera: () => AsyncResult<void, AppError>;
  stopCamera: () => void;
}

export function useWebcam(): UseWebcamReturn {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<AppError | null>(null);

  const startCamera = useCallback(async (): AsyncResult<void, AppError> => {
    // Check if MediaDevices API is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const appError: AppError = {
        errorCode: 'MEDIA_DEVICES_NOT_SUPPORTED',
        errorMessage: 'Camera access is not supported in this browser',
      };
      setError(appError);
      Logger.error('Camera access not supported', {
        resource: 'scanner.camera',
        errorCode: appError.errorCode,
      }, new Error(appError.errorMessage));
      return err(appError);
    }

    // Stop existing stream if any
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }

    setError(null);

    const result = await tryCatchAsync(async () => {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: CAMERA_WIDTH_IDEAL, min: CAMERA_WIDTH_MIN },
          height: { ideal: CAMERA_HEIGHT_IDEAL, min: CAMERA_HEIGHT_MIN },
          frameRate: { ideal: CAMERA_FPS_IDEAL, max: CAMERA_FPS_MAX },
        },
        audio: false,
      });
      return mediaStream;
    });

    return result.match({
      ok: async (mediaStream) => {
        setStream(mediaStream);
        Logger.info('Camera started successfully', {
          resource: 'scanner.camera',
        });
        return ok(undefined);
      },
      err: async (e) => {
        const appError: AppError = {
          errorCode: 'CAMERA_ACCESS_FAILED',
          errorMessage: e instanceof Error ? e.message : 'Failed to access camera',
        };
        setError(appError);
        Logger.error('Camera access failed', {
          resource: 'scanner.camera',
          errorCode: appError.errorCode,
        }, e instanceof Error ? e : new Error(appError.errorMessage));
        return err(appError);
      },
    });
  }, [stream]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
      setError(null);
      Logger.info('Camera stopped', {
        resource: 'scanner.camera',
      });
    }
  }, [stream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  return {
    stream,
    isActive: stream !== null,
    error,
    startCamera,
    stopCamera,
  };
}
